import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import { fetchMiscs } from "../../store/slices/miscsSlice";
import { fetchSEOPages } from "../../store/slices/seoSlice";

import ContactUsContainer from "./components/ContactUsContainer";
import ContactUsHeader from "./components/Header";
import JoinOurTeamContainer from "./components/JoinOurTeamContainer";
import Loader from "../../components/base-components/Loader";
import ErrorMessage from "../../components/base-components/Error";

const ContactUs = () => {
  const [subject, setSubject] = useState<string | null>(null);

  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const { miscs, status, error } = useSelector(
    (state: RootState) => state.miscs
  );

  const {
    seoPages,
    status: seoStatus,
    error: seoError,
  } = useSelector((state: RootState) => state.seoPages);

  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchMiscs());
    }

    if (seoStatus === "idle") {
      dispatch(fetchSEOPages());
    }
  }, [dispatch, status, seoStatus]);

  useEffect(() => {
    if (subject && formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [subject]);

  if (status === "loading" || seoStatus === "loading") {
    return <Loader />;
  }

  if (status === "failed") {
    return <ErrorMessage message={error} />;
  }

  if (seoStatus === "failed") {
    return <ErrorMessage message={seoError} />;
  }

  const getMiscValue = (key: string) => {
    return (
      miscs.find((misc) => misc.attributes.title === key)?.attributes.value ||
      ""
    );
  };

  const getSEOData = () => {
    return seoPages.find(
      (page) => `/${page.attributes.url}` === location.pathname
    )?.attributes;
  };

  const seo = getSEOData();

  return (
    <>
      <Helmet>
        <title>{seo?.seo_title}</title>
        {seo?.seo_description && (
          <meta name="description" content={seo.seo_description} />
        )}

        <link rel="canonical" href={window.location.href} />
        <meta
          property="og:image"
          content="https://www.dancap-group.co.uk/images/dancap-logo-dark.jpg"
        />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        <meta property="og:title" content={seo?.seo_title} />
        <meta property="og:description" content={seo?.seo_description} />
        <meta property="og:url" content="https://www.dancap-group.co.uk" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:image"
          content="https://www.dancap-group.co.uk/images/dancap-logo-dark.jpg"
        />
      </Helmet>
      <ContactUsHeader />
      <ContactUsContainer
        details={getMiscValue("contactus_details")}
        subject={subject}
        formRef={formRef}
      />
      <JoinOurTeamContainer
        description={getMiscValue("joinus_description")}
        setSubject={setSubject}
      />
    </>
  );
};

export default ContactUs;
